import type { ApiResponse, FormSubmissionInput, FormSubmissionResult } from '@saas-bot/types';

const API_URL = import.meta.env.VITE_API_URL ?? '/api';

/**
 * UTM + referrer + landing-page attribution, captured once per session so it
 * survives client navigation and is attached to every form the visitor submits.
 */
export function captureAttribution(): void {
  try {
    if (sessionStorage.getItem('vx_attr')) return;
    const params = new URLSearchParams(window.location.search);
    const attr = {
      utmSource: params.get('utm_source') ?? undefined,
      utmMedium: params.get('utm_medium') ?? undefined,
      utmCampaign: params.get('utm_campaign') ?? undefined,
      utmTerm: params.get('utm_term') ?? undefined,
      utmContent: params.get('utm_content') ?? undefined,
      landingPage: window.location.href,
      referrerUrl: document.referrer || undefined,
    };
    sessionStorage.setItem('vx_attr', JSON.stringify(attr));
  } catch {
    // Private-mode / storage-disabled: attribution is best-effort, never blocking.
  }
}

function readAttribution(): Partial<FormSubmissionInput> {
  try {
    const raw = sessionStorage.getItem('vx_attr');
    return raw ? (JSON.parse(raw) as Partial<FormSubmissionInput>) : {};
  } catch {
    return {};
  }
}

export async function submitForm(
  formKey: string,
  input: FormSubmissionInput,
): Promise<{ ok: boolean; message: string }> {
  const payload: FormSubmissionInput = {
    ...readAttribution(),
    ...input,
    sourceUrl: window.location.href,
  };

  try {
    const response = await fetch(`${API_URL}/public/cms/forms/${formKey}/submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const body = (await response.json()) as ApiResponse<FormSubmissionResult>;
    if (!response.ok) {
      return { ok: false, message: body.message || 'Something went wrong. Please try again.' };
    }
    return { ok: true, message: body.data?.message ?? 'Thanks — we’ll be in touch.' };
  } catch {
    return { ok: false, message: 'Network error. Please try again.' };
  }
}
